// Payment Setup (payee) — the shared, plain-language payout-account onboarding
// panel used by BOTH the Seller and Broker tabs. Drives Stripe Connect Express
// hosted onboarding (a guided, compliant flow of Stripe's own) wrapped in our
// lead-in copy. The api module is injected so this component stays
// account-type-agnostic.
//
// Portal rules: no React Query — useEffect+useState read with a cancelled
// guard; writes via useSubmittingMutation.

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  StatusBadge,
  StatusStepper,
  SubmitButton,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { BankDetailsHelp } from "./BankDetailsHelp";
import type { PayeePaymentSetup, PayeePaymentSetupApi } from "../api/paymentSetup";

const SETUP_STEPS = [
  { key: "start", label: "Start", hint: "Tell us where to send your money." },
  { key: "verify", label: "Verify with Stripe", hint: "Stripe securely collects and verifies your bank details." },
  { key: "done", label: "Done", hint: "Your payout account is ready." },
];

// D1 — backoff schedule (ms before each attempt) for the auto-refresh that runs
// when the browser returns from Stripe hosted onboarding (?connect=return).
// GetPayeeAsync reads the DB, which the account.updated webhook updates a few
// seconds behind the redirect; a bounded live-sync (api.refresh) smooths that
// lag. Cumulative ~2s / 5s / 10s / 18s, capped at 4 attempts (≤4 Stripe
// Accounts.Get). The manual "Refresh status" button stays as the fallback.
// NOTE: ?connect=refresh (an Account Link that expired mid-onboarding) is a
// known follow-up — deliberately NOT handled here.
const RETURN_REFRESH_BACKOFF_MS = [2000, 3000, 5000, 8000];

function stepIndex(status: PayeePaymentSetup["status"]): number {
  if (status === "complete") return 2;
  if (status === "in_progress") return 1;
  return 0;
}

export function PayeePaymentSetupPanel({ api }: { api: PayeePaymentSetupApi }) {
  const toast = useToast();
  const [setup, setSetup] = useState<PayeePaymentSetup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  // Capture the Stripe return marker ONCE so cleaning it from the URL below
  // doesn't drop the auto-refresh on re-render.
  const [returnedFromStripe] = useState(() => params.get("connect") === "return");
  // Latest status, read inside the backoff loop without re-subscribing the effect.
  const statusRef = useRef<PayeePaymentSetup["status"] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get();
        if (!cancelled) setSetup(data);
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "Couldn't load your payout setup.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    statusRef.current = setup?.status ?? null;
  }, [setup]);

  // Consume ?connect=return so a re-render/re-mount can't re-trigger the loop.
  useEffect(() => {
    if (params.get("connect")) {
      const next = new URLSearchParams(params);
      next.delete("connect");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // D1 — on return from Stripe, live-sync the payout account a few times with
  // backoff so the panel advances to "connected" without a manual click. Stops
  // as soon as the account reports complete (or after the bounded attempts).
  useEffect(() => {
    if (!returnedFromStripe) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (i: number) => {
      if (cancelled || i >= RETURN_REFRESH_BACKOFF_MS.length) return;
      timer = setTimeout(async () => {
        if (cancelled || statusRef.current === "complete") return;
        try {
          const data = await api.refresh();
          if (cancelled) return;
          setSetup(data);
          if (data.status === "complete") return; // done — stop polling
        } catch {
          // A transient sync failure shouldn't spin the loop; fall through to
          // the next backoff step. The manual "Refresh status" button remains.
        }
        attempt(i + 1);
      }, RETURN_REFRESH_BACKOFF_MS[i]);
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, returnedFromStripe]);

  // Begin (or resume) Stripe hosted onboarding — the browser leaves the app.
  const start = useSubmittingMutation(
    async () => {
      const { url } = await api.onboardingLink();
      window.location.href = url;
    },
    {
      onError: (err) =>
        toast.error(
          err instanceof Error
            ? err.message
            : "We couldn't open the setup page. Please try again.",
        ),
    },
  );

  const refresh = useSubmittingMutation(
    async () => {
      const data = await api.refresh();
      setSetup(data);
    },
    {
      onSuccess: () => toast.success("Status updated."),
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : "Couldn't refresh your status.",
        ),
    },
  );

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert" data-testid="payee-payment-setup-error">
        {error}
      </p>
    );
  }
  if (!setup) {
    return <Skeleton className="h-40 w-full" data-testid="payee-payment-setup-loading" />;
  }

  const isComplete = setup.status === "complete";

  return (
    <div className="space-y-space-4" data-testid="payee-payment-setup">
      <StatusStepper steps={SETUP_STEPS} currentIndex={stepIndex(setup.status)} complete={isComplete} />

      {isComplete ? (
        <ConnectedState
          setup={setup}
          onManage={() => void start.submit()}
          managePending={start.pending}
        />
      ) : (
        <Card>
          <CardContent className="space-y-space-3">
            <div className="space-y-space-1">
              <h3 className="text-h4 font-semibold text-onyx m-0">
                Set up your payout account
              </h3>
              <p className="text-small text-gray-mid m-0">
                This is where we&apos;ll send the money you&apos;re owed. Our
                payment partner Stripe will securely collect your bank details and
                verify your identity — it usually takes just a few minutes. Have
                your bank details handy before you start.
              </p>
            </div>
            {setup.status === "in_progress" && (
              <div
                className="rounded-lg border border-status-warning bg-white-warm p-space-3"
                data-testid="payee-payment-setup-resume"
              >
                <StatusBadge tone="warn">Almost there</StatusBadge>
                <p className="mt-space-2 text-small text-onyx m-0">
                  You&apos;ve started setup but Stripe still needs a little more
                  information. Continue where you left off, or refresh below if you
                  just finished.
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-space-2">
              <Button
                variant="primary"
                size="sm"
                loading={start.pending}
                disabled={start.pending}
                onClick={() => void start.submit()}
                data-testid="payee-payment-setup-start"
              >
                {setup.status === "in_progress"
                  ? "Continue setup"
                  : "Set up your payout account"}
              </Button>
              <SubmitButton
                type="button"
                variant="secondary"
                size="sm"
                pending={refresh.pending}
                pendingLabel="Checking…"
                onClick={() => void refresh.submit()}
                data-testid="payee-payment-setup-refresh"
              >
                Refresh status
              </SubmitButton>
            </div>
            <p className="text-caption text-gray-mid m-0">
              You&apos;ll be taken to Stripe&apos;s secure setup page and brought
              back here when you&apos;re done.
            </p>
          </CardContent>
        </Card>
      )}

      <BankDetailsHelp context="payee" />
    </div>
  );
}

function ConnectedState({
  setup,
  onManage,
  managePending,
}: {
  setup: PayeePaymentSetup;
  onManage: () => void;
  managePending: boolean;
}) {
  return (
    <Card data-testid="payee-payment-setup-complete">
      <CardContent className="space-y-space-3">
        <div className="flex items-center gap-space-2">
          <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
          <StatusBadge tone="ok">Payout account connected</StatusBadge>
        </div>
        <p className="text-small text-onyx m-0">
          You&apos;re all set to receive payouts
          {setup.bankName ? (
            <>
              {" "}
              to <span className="font-semibold">{setup.bankName}</span>
              {setup.accountLast4 && (
                <>
                  {" "}
                  <span className="font-semibold">••••{setup.accountLast4}</span>
                </>
              )}
            </>
          ) : null}
          . There&apos;s nothing more to do right now.
        </p>
        <Button
          variant="secondary"
          size="sm"
          loading={managePending}
          disabled={managePending}
          onClick={onManage}
          data-testid="payee-payment-setup-manage"
        >
          Manage / update
        </Button>
      </CardContent>
    </Card>
  );
}
